import { CellContext } from "@tanstack/react-table";
import type { ViewTableFeatures } from "@design-system/ViewTable/core/table";
import { memo, useCallback, useState } from "react";
import classNames from "classnames";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { ViewTableCell } from "@design-system/ViewTable/ViewTableCell";
import viewTableStyles from "@design-system/ViewTable/ViewTable.module.scss";
import { useRevokeMcpConnectionMutation } from "@api/mcpConnections.api";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import type { McpConnectionRow } from "../mcpConnectionsViewConfig";
import styles from "../McpCliSettings.module.scss";

export const ConnectionActionsCell = memo(
    ({ row }: CellContext<ViewTableFeatures, McpConnectionRow, string>) => {
        const revokeMutation = useRevokeMcpConnectionMutation();
        const { error: toastError } = useToaster();
        const [showConfirmation, setShowConfirmation] = useState(false);
        const connection = row.original;

        const confirmRevoke = useCallback(async () => {
            try {
                await revokeMutation.mutateAsync(connection.id);
                setShowConfirmation(false);
            } catch {
                toastError("Failed to revoke connection");
            }
        }, [revokeMutation, toastError, connection.id]);

        if (connection.revokedAt) {
            return <ViewTableCell />;
        }

        return (
            <ViewTableCell
                captureClicks
                className={classNames(
                    styles.actionCell,
                    viewTableStyles.showOnHover,
                )}
            >
                <ButtonWithLoader
                    variant="primary"
                    size="sm"
                    loading={revokeMutation.isPending}
                    onClick={() => setShowConfirmation(true)}
                >
                    Revoke
                </ButtonWithLoader>
                <ConfirmationModal
                    show={showConfirmation}
                    onHide={() => setShowConfirmation(false)}
                    closeButton
                    title="Revoke connection?"
                    question={`This will revoke access for "${connection.clientName}". This cannot be undone.`}
                    onConfirm={confirmRevoke}
                    onReject={() => setShowConfirmation(false)}
                    yes="Revoke"
                    yesBtnVariant="destructive"
                    no="Cancel"
                />
            </ViewTableCell>
        );
    },
);
